import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { getProtocolPriorityColor, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ProtocolTypeTag, Tag } from "@msuite/ui/tag";
import { moment } from "@msuite/utils/date";
import { getProtocolPriorityTranslationKey, getProtocolTypeColor } from "@msuite/utils/protocols";
import { guardString } from "@msuite/utils/strings";
import React from "react";
import { ContactMemberCard, ProjectCardMention, UserCard } from "@/components/cards";
import { useProtocol } from "@/hooks/use-protocol";
import type { ProtocolPanelBaseProps } from "../shared";

export function ProtocolInformation({ protocolId }: ProtocolPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { protocol } = useProtocol(protocolId);

	/** Computed */
	const hasTime = Boolean(protocol?.startTime || protocol?.endTime);
	const priority = protocol?.callRequestPriority;

	/** Guard */
	if (!protocol) return null;

	/** Render */
	return (
		<ContentCard
			icon={icon("Info")}
			title={t("general.information")}
		>
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("protocols.type")}</DataGrid.Label>
					<DataGrid.Cell>
						<ProtocolTypeTag
							type={protocol.type}
							color={getProtocolTypeColor(protocol.type)}
							variant="plain"
							size="md"
						/>
					</DataGrid.Cell>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("dates.date")}</DataGrid.Label>
					<DataGrid.Value>
						{protocol.date ? moment(protocol.date).dateFormat() : guardString(null)}
					</DataGrid.Value>
				</DataGrid.Row>
				{hasTime && (
					<DataGrid.Row>
						<DataGrid.Label>{t("dates.time")}</DataGrid.Label>
						<DataGrid.Value>
							{`${guardString(protocol.startTime)} - ${guardString(protocol.endTime)}`}
						</DataGrid.Value>
					</DataGrid.Row>
				)}
				<DataGrid.Divider />
				<DataGrid.Row>
					<DataGrid.Label>{t("protocols.context")}</DataGrid.Label>
					<DataGrid.Cell>
						{protocol.projectId ? (
							<ProjectCardMention projectId={protocol.projectId} />
						) : (
							<p>{guardString(protocol.description)}</p>
						)}
					</DataGrid.Cell>
				</DataGrid.Row>
				{protocol.type === "call-request" && (
					<React.Fragment>
						<DataGrid.Divider />
						<DataGrid.Row>
							<DataGrid.Label>{t("protocols.caller")}</DataGrid.Label>
							<DataGrid.Cell>
								{protocol.callRequestBy ? (
									<ContactMemberCard
										contactMemberId={protocol.callRequestBy}
										variant="plain"
										fontSize="md"
									/>
								) : (
									<p>{guardString(null)}</p>
								)}
							</DataGrid.Cell>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("protocols.call_request_for_user")}</DataGrid.Label>
							<DataGrid.Cell>
								<UserCard
									fontSize="md"
									withAvatar={false}
									userId={protocol.callRequestFor}
									variant="plain"
								/>
							</DataGrid.Cell>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("priority.priority")}</DataGrid.Label>
							<DataGrid.Cell>
								{priority ? (
									<Tag
										variant="plain"
										color={getProtocolPriorityColor(priority)}
										size="md"
									>
										{t(getProtocolPriorityTranslationKey(priority))}
									</Tag>
								) : (
									<p>{guardString(null)}</p>
								)}
							</DataGrid.Cell>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("protocols.callback_time")}</DataGrid.Label>
							<DataGrid.Value>
								{protocol.callRequestCallbackTime
									? moment(protocol.callRequestCallbackTime).longFormat()
									: guardString(null)}
							</DataGrid.Value>
						</DataGrid.Row>
					</React.Fragment>
				)}
				<DataGrid.Divider />
				<DataGrid.Row>
					<DataGrid.Label>{t("general_fields.created_by")}</DataGrid.Label>
					<DataGrid.Cell>
						<UserCard
							fontSize="md"
							withAvatar={false}
							userId={protocol.createdBy}
							variant="plain"
						/>
					</DataGrid.Cell>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("general_fields.created_at")}</DataGrid.Label>
					<DataGrid.Value>{moment(protocol.createdAt).longFormat()}</DataGrid.Value>
				</DataGrid.Row>
			</DataGrid>
		</ContentCard>
	);
}
